/**
 * Customer Hooks
 */

import React from "react";
import {
	type Customer,
	type Address,
	type CustomerPackage,
	type CustomerServiceType,
	type CustomerType,
	type Log,
	type CustomerServiceTypeAttribute,
	type CustomerService,
	type Email,
	type Numbers,
	type UsageProgression,
	type Session,
	type Note,
	type BankAccount,
	type Setbox,
	type Attribute,
	type CustomerAttribute,
	type CustomerStatus,
	type CustomerChanges,
	type SimCard,
	type CustomerPackageChange,
	type CustomerPassword,
	type CustomerPrefix,
	type Account,
	NewCustomer,
	CustomerNotification,
	CustomerAccount,
} from "@/types/customer";
import { InternetService } from "@/types/internet";
import { Package, Product } from "@/types/product";
import { ContractService, InternetServiceInContract } from "@/types/contract";
import { z } from "zod";

export const updateCustomerAccountPassword = async (
	accountId: string,
	newPassword: string,
	params?: {
		sendEmail?: boolean;
		sendSMS?: boolean;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/account/${accountId}/password`,
		);
		const res = await fetch(url.toString(), {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ password: newPassword, ...params }),
		});

		const data = await res.json();
		if (data.success) {
			return data.message ?? "Нууц үг амжилттай солигдлоо";
		} else {
			throw new Error(data.message ?? "Нууц үг солих үед алдаа гарлаа");
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error.message ?? "Нууц үг солих үед алдаа гарлаа");
	}
};

export const checkValidEmail = async (
	email: string,
): Promise<{ isValid: boolean; inUse: boolean; email: string }[]> => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/utils/email-validate`,
		);
		url.searchParams.append("email", email);
		const res = await fetch(url.toString());

		const data = await res.json();

		if (data.data) {
			return data.data;
		} else {
			throw new Error(data?.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		throw error;
	}
};

export const checkValidPhoneNumber = async (
	number: string,
): Promise<
	{
		isValid: boolean;
		number: string;
		inUse: boolean;
	}[]
> => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/utils/phone-number-validate`,
		);
		url.searchParams.append("number", number);

		const res = await fetch(url.toString());

		const data = await res.json();

		if (data.data) {
			return data.data;
		} else {
			throw new Error(data?.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		throw error;
	}
};

export const fetchSubscriptionCount = async (
	period?: "daily" | "weekly" | "monthly" | "yearly" | "allTime",
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/subscription-count`,
		);
		if (period) {
			url.searchParams.append("period", period);
		}

		const res = await fetch(url.toString());

		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw error;
	}
};

export const fetchCustomerStats = async () => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/customer/stats`);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as {
				all: number;
				active: number;
				suspended: number;
				cancelled: number;
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw error;
	}
};

export const fetchCustomerNotifications = async (
	customerId: string,
	params?: {
		page: number;
		limit: number;
		search?: string;
		dateFrom?: string;
		dateTo?: string;
	},
) => {
	const url = new URL(
		`${process.env.REACT_APP_API_URL}/customer/${customerId}/notifications`,
	);
	if (params?.page !== undefined) {
		url.searchParams.append("page", params.page.toString());
	}
	if (params?.limit) {
		url.searchParams.append("limit", params.limit.toString());
	}
	if (params?.search) {
		url.searchParams.append("search", params.search);
	}
	if (params?.dateFrom) {
		url.searchParams.append("dateFrom", params.dateFrom);
	}
	if (params?.dateTo) {
		url.searchParams.append("dateTo", params.dateTo);
	}

	try {
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				notifications: data.data as CustomerNotification[],
				total: data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const NewCustomerSchema = z.object({
	type: z.enum(["individual", "company"]),
	name: z.string().min(1, "Нэр оруулна уу").optional().nullable(),
	firstName: z.string().min(1, "Нэр оруулна уу").optional().nullable(),
	lastName: z.string().min(1, "Овог оруулна уу").optional().nullable(),
	registrationNumber: z.string().min(1, "Регистерийн дугаар оруулна уу"),
	gender: z.enum(["male", "female", "other"]).optional().nullable(),
	birthday: z.string().optional().nullable(),
	emails: z
		.array(
			z.object({
				email: z.string().email("Имэйл хаяг буруу байна"),
				isPrimary: z.boolean().optional().default(false),
			}),
		)
		.min(1, {
			message: "Имэйл хаяг оруулна уу",
		})
		.max(3, {
			message: "Имэйл хаяг 3-аас их байж болохгүй",
		}),
	numbers: z
		.array(
			z.object({
				number: z.string().min(1, "Утасны дугаар оруулна уу"),
				isPrimary: z.boolean().optional().default(false),
			}),
		)
		.min(1, {
			message: "Утасны дугаар оруулна уу",
		})
		.max(3, {
			message: "Утасны дугаар 3-аас их байж болохгүй",
		}),
	contract: z.object({
		templateId: z.string().min(1, "Гэрээний загвар сонгоно уу"),
		label: z.string().min(1, "Гэрээний нэр оруулна уу"),
		description: z.string().optional().nullable(),
		hasCommitment: z.boolean().optional().default(false),
		commitmentMonths: z.number().optional().nullable(),
		balance: z
			.number()
			.min(1, "Төлбөрийн үлдэгдэл оруулна уу")
			.optional()
			.nullable(),
		priceOverride: z.boolean().optional().default(false),
		price: z.number().min(0, "Үнэ оруулна уу").optional().nullable(),
		cycle: z
			.enum(["monthly", "quarterly", "yearly"])
			.optional()
			.default("monthly"),
		period: z.number().min(1, "Хугацаа оруулна уу").optional().nullable(),
		hasTrial: z.boolean().optional().default(false),
		trialCycle: z
			.enum(["daily", "monthly", "quarterly", "yearly"])
			.optional()
			.default("monthly"),
		trialPeriod: z
			.number()
			.min(1, "Суурь хугацаа оруулна уу")
			.optional()
			.nullable(),
		packages: z
			.array(
				z.object({
					id: z.string().min(1, "Багц сонгоно уу"),
				}),
			)
			.min(1, {
				message: "Багц сонгоно уу",
			}),
		internetServiceId: z
			.string()
			.min(1, "Интернэт үйлчилгээ сонгоно уу")
			.optional()
			.nullable(),
		additionalServices: z
			.array(
				z.object({
					id: z.string().min(1, "Үйлчилгээ сонгоно уу"),
				}),
			)
			.optional()
			.nullable(),
	}),
});

export const fetchEasyVAT = async (
	customerId: string,
	registrationNumber: string,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/easy-vat`,
		);
		const res = await fetch(url.toString(), {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ registrationNumber }),
		});

		const data = await res.json();
		if (data.success) {
			return data.data.loginName as string;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error.message ?? "Алдаа гарлаа");
	}
};

export const useCreateCustomer = () => {
	const [data, setData] = React.useState<NewCustomer>({} as NewCustomer);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string | null>(null);

	const createCustomer = React.useCallback(
		async (customerData: NewCustomer) => {
			setLoading(true);
			setError(null);

			// try {
			// 	NewCustomerSchema.parse(customerData)
			// } catch (error) {
			// 	if(error instanceof z.ZodError) {
			// 		const errorMessage = error.errors.map(err => err.message).join(', ')
			// 		setError(errorMessage)
			// 		setLoading(false)
			// 		throw new Error(errorMessage)
			// 	}
			// }

			try {
				const url = new URL(
					`${process.env.REACT_APP_API_URL}/customer/create-new`,
				);
				const res = await fetch(url.toString(), {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						...customerData,
						individual:
							customerData.type === "individual"
								? {
										firstName: customerData.firstName,
										lastName: customerData.lastName,
										birthday: customerData.birthday,
										gender: customerData.gender,
										registrationNumber: customerData.registrationNumber,
									}
								: undefined,
						company:
							customerData.type !== "individual"
								? {
										name: customerData.name,
										registrationNumber: customerData.registrationNumber,
									}
								: undefined,
					}),
				});

				const data = await res.json();

				if (data.success) {
					setData(data.data.customer as NewCustomer);
					return data.data as {
						customerId: string;
						systemId: string;
						contractId?: string;
					};
				} else {
					throw new Error(data.message ?? "Алдаа гарлаа");
				}
			} catch (error: any) {
				console.error(error);
				setError(error.message || "Алдаа гарлаа");
				throw error;
			} finally {
				setLoading(false);
			}
		},
		[],
	);

	return { data, loading, error, createCustomer };
};

export const fetchCustomerPrefixes = async () => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/prefix/list`,
		);
		const res = await fetch(url.toString());

		const data = await res.json();

		if (data.success) {
			return data.data as CustomerPrefix[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error ?? "Алдаа гарлаа");
	}
};

export const fetchCustomerPrefix = async (prefixId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/prefix/${prefixId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerPrefix;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error ?? "Алдаа гарлаа");
	}
};

export const fetchCustomerPassword = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/password`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerPassword;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error ?? "Алдаа гарлаа");
	}
};

export const fetchCustomerPackageChanges = async (
	customerId: string,
	params?: {
		page?: number;
		rowsPerPage?: number;
		packageId?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/package/status/changes`,
		);

		if (params?.packageId) {
			url.searchParams.append("packageId", params.packageId);
		}

		if (params?.page !== undefined) {
			url.searchParams.append("page", params.page.toString());
		}

		if (params?.rowsPerPage) {
			url.searchParams.append("limit", params.rowsPerPage.toString());
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				changes: data.data.changes as CustomerPackageChange[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerSims = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/sims`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as SimCard[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerChange = async (
	customerId: string,
	changeId: string,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/change/${changeId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerChanges;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerChanges = async (
	customerId: string,
	params?: {
		limit?: number;
		page?: number;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/status/changes`,
		);

		if (params?.limit) {
			url.searchParams.append("limit", params.limit.toString());
		}

		if (params?.page !== undefined) {
			url.searchParams.append("page", params.page.toString());
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				changes: data.data.changes as CustomerChanges[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerStatus = async (
	customerId: string,
	params?: {
		types?: string[];
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/status`,
		);

		if (params?.types) {
			url.searchParams.append("types", params.types.join(","));
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerStatus[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerAttributes = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/attributes`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerAttribute[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerAttribute = async (
	customerId: string,
	attributeId: string,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/attribute/${attributeId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerAttribute;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchAttributes = async (params?: {
	limit?: number;
	page?: number;
	search?: string;
}) => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/customer/attributes`);

		if (params?.limit) {
			url.searchParams.append("limit", params.limit.toString());
		}

		if (params?.page !== undefined) {
			url.searchParams.append("page", params.page.toString());
		}

		if (params?.search) {
			url.searchParams.append("search", params.search);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				attributes: data.data.attributes as Attribute[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerInternetService = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/internet-services`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as InternetServiceInContract[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerDevices = async (
	customerId: string,
	params?: {
		page?: number;
		limit?: number;
		search?: string;
		contractId?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/devices`,
		);

		if (params?.page !== undefined) {
			url.searchParams.append("page", params.page.toString());
		}

		if (params?.limit) {
			url.searchParams.append("limit", params.limit.toString());
		}

		if (params?.search) {
			url.searchParams.append("search", params.search);
		}

		if (params?.contractId) {
			url.searchParams.append("contractId", params.contractId);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				devices: data.data.devices as Setbox[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractList = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/contract`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchBankAccounts = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/bank-accounts`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as BankAccount[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchBankAccount = async (accountId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/bank-account/${accountId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as BankAccount;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerNotes = async (
	customerId: string,
	params?: {
		page?: number;
		limit?: number;
		search?: string;
		invoiceId?: string;
		contractId?: string;
		customerPackageId?: string;
		contractServiceId?: string;
		deviceId?: string;
		leaseId?: string;
		pinned?: boolean;
		from?: string;
		to?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/note`,
		);

		if (params?.page !== undefined) {
			url.searchParams.append("page", params.page.toString());
		}

		if (params?.limit) {
			url.searchParams.append("limit", params.limit.toString());
		}

		if (params?.search) {
			url.searchParams.append("search", params.search);
		}

		if (params?.invoiceId) {
			url.searchParams.append("invoiceId", params.invoiceId);
		}

		if (params?.contractId) {
			url.searchParams.append("contractId", params.contractId);
		}

		if (params?.customerPackageId) {
			url.searchParams.append("customerPackageId", params.customerPackageId);
		}

		if (params?.contractServiceId) {
			url.searchParams.append("contractServiceId", params.contractServiceId);
		}

		if (params?.deviceId) {
			url.searchParams.append("deviceId", params.deviceId);
		}

		if (params?.pinned) {
			url.searchParams.append("pinned", params.pinned ? "1" : "0");
		}

		if (params?.from) {
			url.searchParams.append("from", params.from);
		}

		if (params?.to) {
			url.searchParams.append("to", params.to);
		}

		if (params?.leaseId) {
			url.searchParams.append("leaseId", params.leaseId);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				notes: data.data.notes as Note[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

interface UsageProgressionParams {
	customerId: string;
}

export const fetchUsageProgression = async (params: UsageProgressionParams) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${params.customerId}/usage`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as UsageProgression;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerPackageDetails = async (
	customerPackageId: string,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/package/${customerPackageId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerPackage;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		throw new Error("Алдаа гарлаа");
	}
};

interface CustomerPackageParams {
	connectionId: string;
	customerId: string;
}

export const fetchCustomerPackage = async (params: CustomerPackageParams) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${params.customerId}/package/${params.connectionId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data.package as CustomerPackage;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

interface AddressParams {
	addressId: string;
}

export const fetchAddress = async (params: AddressParams) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/address/${params.addressId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as Address;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

interface CustomerAddressParams {
	customerId: string;
}

export const fetchCustomerAddress = async (params: CustomerAddressParams) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${params.customerId}/address`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as Address[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

interface Params {
	search?: string;
	page?: number;
	limit?: number;
	customerType?: string;
	status?: "active" | "suspended" | "canceled" | "all";
	contractId?: string;
	ids?: string[];
}

export const fetchCustomers = async (params: Params) => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/customer/list`);

		if (params.ids && params.ids.length > 0) {
			url.searchParams.append("ids", params.ids.join(","));
		}

		if (params.search) {
			url.searchParams.append("search", params.search);
		}

		if (params.page !== undefined) {
			url.searchParams.append("page", params.page.toString());
		}

		if (params.limit) {
			url.searchParams.append("limit", params.limit.toString());
		}

		if (params.customerType) {
			url.searchParams.append("type", params.customerType);
		}

		if (params.status) {
			url.searchParams.append("status", params.status);
		}

		if (params.contractId) {
			url.searchParams.append("contractId", params.contractId);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				customers: data.data as Customer[],
				total: data.count as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerFullDetails = async (
	customerId: string,
	contractId?: string,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/full-details`,
		);
		if (contractId) {
			url.searchParams.append("contractId", contractId);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		throw error;
	}
};

export const fetchCustomer = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data.customer as Customer;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerEmails = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/emails`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data.emails as Email[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerNumbers = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/numbers`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data.numbers as Numbers[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerAccounts = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/accounts`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as Account[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchUserLog = async (
	customerId: string,
	{
		page = 1,
		limit = 10,
		group = "all",
		from,
		to,
	}: {
		page: number;
		limit: number;
		group?: "all" | "day" | "month" | "year";
		from?: string;
		to?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/log`,
		);
		url.searchParams.append("page", page.toString());
		url.searchParams.append("limit", limit.toString());
		url.searchParams.append("group", group);
		if (from) {
			url.searchParams.append("from", from);
		}
		if (to) {
			url.searchParams.append("to", to);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				logs: data.data.logs as Log[],
				total: data.data.total as number,
				days: data.data.days as {
					[key: string]: Log[];
				},
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerPackages = async (
	customerId: string,
	params?: {
		page?: number;
		limit?: number;
		priority?: "primary" | "secondary";
		search?: string;
		status?:
			| "active"
			| "inactive"
			| "cancelled"
			| "suspended"
			| "broken"
			| "expired"
			| "all";
		contractId?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/packages`,
		);

		if (params?.page !== undefined) {
			url.searchParams.append("page", params.page.toString());
		}

		if (params?.limit) {
			url.searchParams.append("limit", params.limit.toString());
		}

		if (params?.priority) {
			url.searchParams.append("priority", params.priority);
		}

		if (params?.search) {
			url.searchParams.append("search", params.search);
		}

		if (params?.status) {
			url.searchParams.append("status", params.status);
		}

		if (params?.contractId) {
			url.searchParams.append("contractId", params.contractId);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				packages: data.data.packages as CustomerPackage[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerProducts = async (
	customerId: string,
	{
		page = 1,
		limit = 10,
	}: {
		page: number;
		limit: number;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/products`,
		);

		url.searchParams.append("page", page.toString());
		url.searchParams.append("limit", limit.toString());

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				products: data.data.products as Product[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerTypes = async () => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/customer/type`);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data.types as CustomerType[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerType = async (typeId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/type/${typeId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerType;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerCount = async (params: { type?: string }) => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/customer/count`);
		if (params.type) {
			url.searchParams.append("type", params.type);
		}
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data.count as number;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerServiceTypes = async () => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/customer-service`);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerServiceType[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerService = async (serviceId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer-service/${serviceId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerService;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerServiceAttributes = async (serviceId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer-service/${serviceId}/attribute`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerServiceTypeAttribute[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerServiceAttribute = async (
	serviceId: string,
	attributeId: string,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer-service/${serviceId}/attribute/${attributeId}`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerServiceTypeAttribute;
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerAdditionalServices = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/additional-services`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				services: data.data.services as ContractService[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerServices = async (customerId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/customer/${customerId}/services`,
		);
		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as CustomerService[];
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchCustomerSessions = async ({
	page = 1,
	limit = 10,
	search = "",
}: {
	page: number;
	limit: number;
	search?: string;
}) => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/customer/session`);
		url.searchParams.append("page", page.toString());
		url.searchParams.append("limit", limit.toString());
		url.searchParams.append("search", search);

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				sessions: data.data.sessions as Session[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message ?? "Алдаа гарлаа");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};
